import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tabs, Spin, message, Typography } from 'antd'
import axios from 'axios'
import { CnCaFile, CnCaFileInit } from './components/CnCaFile'
import { CollToAgencyOrRg2, CollToAgencyOrRg2Init } from './components/CollToAgencyOrRg2'
import CnApeLabels, { ApeCode } from './components/CnApeLabels'

export type CnOrgaDataTab = 'caFile' | 'collsAgenciesOrRg2' | 'ape'

interface CnOrgaDataPageProps {
  orgaId: string
  initialTab?: CnOrgaDataTab
  /** When embedded in softaccess page, sync sub-tab via `sub` query param (parent uses `tab`). */
  embedded?: boolean
}

interface ApeInitData {
  apeCodes: ApeCode[]
}

function isCnOrgaDataTab(value: string | null): value is CnOrgaDataTab {
  return value === 'caFile' || value === 'collsAgenciesOrRg2' || value === 'ape'
}

function resolveInitialTab(initialTab?: CnOrgaDataTab, paramName = 'tab'): CnOrgaDataTab {
  if (initialTab) return initialTab
  const params = new URLSearchParams(window.location.search)
  const tab = params.get(paramName)
  if (isCnOrgaDataTab(tab)) return tab
  return 'collsAgenciesOrRg2'
}

export function CnOrgaDataPage({ orgaId, initialTab, embedded = false }: CnOrgaDataPageProps) {
  const subTabParam = embedded ? 'sub' : 'tab'
  const [activeTab, setActiveTab] = useState<CnOrgaDataTab>(() =>
    resolveInitialTab(initialTab, subTabParam),
  )

  const [caFileData, setCaFileData] = useState<CnCaFileInit | null>(null)
  const [filesLoading, setFilesLoading] = useState(false)
  const [filesLoaded, setFilesLoaded] = useState(false)

  const [collsData, setCollsData] = useState<CollToAgencyOrRg2Init | null>(null)
  const [collsLoading, setCollsLoading] = useState(false)
  const [collsLoaded, setCollsLoaded] = useState(false)

  const [apeData, setApeData] = useState<ApeInitData | null>(null)
  const [apeLoading, setApeLoading] = useState(false)
  const [apeLoaded, setApeLoaded] = useState(false)

  const loadFiles = useCallback(async () => {
    setFilesLoading(true)
    try {
      const { data } = await axios.get<CnCaFileInit>(
        `/organisations/${orgaId}/datacnegoce/cafile/init`,
      )
      setCaFileData(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les fichiers Data')
    } finally {
      setFilesLoading(false)
      setFilesLoaded(true)
    }
  }, [orgaId])

  const loadColls = useCallback(async () => {
    setCollsLoading(true)
    try {
      const { data } = await axios.get<CollToAgencyOrRg2Init>(
        `/organisations/${orgaId}/datacnegoce/colltoagency/init`,
      )
      setCollsData(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les associations Rang 3 / Pdv')
    } finally {
      setCollsLoading(false)
      setCollsLoaded(true)
    }
  }, [orgaId])

  const loadApe = useCallback(async () => {
    setApeLoading(true)
    try {
      const { data } = await axios.get<ApeInitData>(
        `/organisations/${orgaId}/datacnegoce/cnOrgaData/init`,
      )
      setApeData(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les libellés APE')
    } finally {
      setApeLoading(false)
      setApeLoaded(true)
    }
  }, [orgaId])

  useEffect(() => {
    if (activeTab === 'caFile' && !filesLoaded && !filesLoading) loadFiles()
    if (activeTab === 'collsAgenciesOrRg2' && !collsLoaded && !collsLoading) loadColls()
    if (activeTab === 'ape' && !apeLoaded && !apeLoading) loadApe()
  }, [
    activeTab,
    filesLoaded,
    filesLoading,
    collsLoaded,
    collsLoading,
    apeLoaded,
    apeLoading,
    loadFiles,
    loadColls,
    loadApe,
  ])

  const handleTabChange = (key: string) => {
    const tab = key as CnOrgaDataTab
    setActiveTab(tab)
    const url = new URL(window.location.href)
    if (tab === 'collsAgenciesOrRg2') {
      url.searchParams.delete(subTabParam)
    } else {
      url.searchParams.set(subTabParam, tab)
    }
    window.history.replaceState({}, '', url.toString())
  }

  const items = useMemo(
    () => [
      {
        key: 'collsAgenciesOrRg2',
        label: (
          <Typography>
            Accès aux agences
          </Typography>
        ),
        children: (
          <Spin spinning={collsLoading}>
            {collsData ? (
              <CollToAgencyOrRg2 orgaId={orgaId} initData={collsData} />
            ) : (
              <div style={{ minHeight: 120 }} />
            )}
          </Spin>
        ),
      },
      {
        key: 'caFile',
        label: (
          <Typography>
            Fichier CA
          </Typography>
        ),
        children: (
          <Spin spinning={filesLoading}>
            {caFileData ? (
              <CnCaFile
                orgaId={orgaId}
                initData={caFileData}
                onUploaded={loadFiles}
              />
            ) : (
              <div style={{ minHeight: 120 }} />
            )}
          </Spin>
        ),
      },
      {
        key: 'ape',
        label: (
          <Typography>
            APE : libellés & taux d'achats accessibles
          </Typography>
        ),
        children: (
          <Spin spinning={apeLoading}>
            {apeData ? (
              <CnApeLabels apeCode={apeData.apeCodes} orgaId={orgaId} />
            ) : (
              <div style={{ minHeight: 120 }} />
            )}
          </Spin>
        ),
      },
    ],
    [
      orgaId,
      filesLoading,
      caFileData,
      collsLoading,
      collsData,
      apeLoading,
      apeData,
      loadFiles,
    ],
  )

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={items}
        size="large"
        rootClassName="cn-orga-data-tabs"
      />
      <style>
        {`
          .cn-orga-data-tabs .ant-tabs-nav-wrap {
            justify-content: flex-end;
          }
        `}
      </style>
    </>
  )
}
